import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChildren,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    QueryList,
    SimpleChanges
} from '@angular/core';

import { BaseComponent, CoreFacade } from '@ridango/core';

import { filter, takeUntil, tap } from 'rxjs/operators';

import { LayerGroupType, MapApiService } from '../services';
import { MapMarkerGroupComponent } from './map-marker-group.component';
import { MapMarkerComponent } from './map-marker.component';
import { MapPolylineComponent } from './map-polyline.component';

import { getMapSettings } from '@ridango/components/lib/localStorage';

@Component({
    selector: 'rwidget-map-layer-group',
    template: ``,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapLayerGroupComponent extends BaseComponent implements OnInit, OnDestroy, AfterContentInit, OnChanges {
    private mapReady$ = this.mapApi.mapReady().pipe(filter((ready) => ready));
    private layerGroup: LayerGroupType;

    /**
     * Label to show in map control. If not provided, this layer will be absent from map controls.
     */
    @Input() label: string;

    /**
     * Name to interact with localStorage map settings.
     */
    @Input() name?: string;

    /**
     * Set false to hide layer by default. Will only work for layers with labels. Unlabelled layers will show on the
     * map irrespective of the value of this input.
     */
    @Input() showOnMap = true;

    /**
     * Sets the map view to contain all the markers within the layer group
     */
    @Input() fit = false;

    @ContentChildren(MapMarkerComponent) markers: QueryList<MapMarkerComponent>;

    @ContentChildren(MapPolylineComponent) polylines: QueryList<MapPolylineComponent>;

    @ContentChildren(MapMarkerGroupComponent) markerGroups: QueryList<MapMarkerGroupComponent>;

    constructor(
        private mapApi: MapApiService,
        private zone: NgZone,
        private coreFacade: CoreFacade
    ) {
        super();
    }

    ngOnInit(): void {
        this.zone.runOutsideAngular(() => {
            this.layerGroup = this.mapApi.createLayerGroup();

            if (this.label) {
                this.mapApi.addToControls(this.label, this.layerGroup);
                this.mapApi.addToOverlays(this.name, this.label, this.layerGroup);
            }
        });

        // Wait for app initialization to complete before subscribing to language changes.
        // During startup, SET_REGION resets selectedLanguage to null, then after 1000ms
        // SET_SELECTED_LANGUAGE sets the actual language (app-startup.service.ts:229).
        // This delay prevents overlays from being removed during that initialization sequence.
        // TODO: Refactor language state management to avoid this timing dependency. LPAD-8715
        setTimeout(() => {
            let previousLanguage: any = null;

            this.coreFacade.selectedLanguage$
                .pipe(
                    tap((currentLanguage) => {
                        if (previousLanguage !== null && previousLanguage !== currentLanguage) {
                            this.mapApi.removeFromControls(this.layerGroup);
                            this.mapApi.removeFromOverlays(this.name);
                        }
                        previousLanguage = currentLanguage;
                    }),
                    takeUntil(this.componentDestroyed$)
                )
                .subscribe();
        }, 1000);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { label } = changes;

        /** work around for translated label input not showing up on first load */
        if (label && label.currentValue && this.layerGroup) {
            this.zone.runOutsideAngular(() => {
                this.mapApi.addToOverlays(this.name, this.label, this.layerGroup);
                this.mapApi.addToControls(this.label, this.layerGroup);
            });
        }
    }

    ngAfterContentInit(): void {
        this.mapReady$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => this.init());
    }

    ngOnDestroy(): void {
        this.zone.runOutsideAngular(() => {
            if (this.label) {
                this.mapApi.removeFromControls(this.layerGroup);
                this.mapApi.removeFromOverlays(this.name);
            }

            this.mapApi.removeLayerGroup(this.layerGroup);
        });

        super.ngOnDestroy();
    }

    private init(): void {
        this.zone.runOutsideAngular(() => {
            const isShowOnInit = this.name ? !getMapSettings()?.hiddenOverlays?.includes(this.name) : this.showOnMap;
            if (isShowOnInit) {
                this.mapApi.addToMap(this.layerGroup);
            }

            this.addMarkers();
            this.markers.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => this.addMarkers());

            this.addMarkerGroups();
            this.markerGroups.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => this.addMarkerGroups());

            this.addPolyline();
            this.polylines.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => this.addPolyline());

            let bounds = [];
            this.markers.forEach((m) => {
                this.mapApi.addToLayerGroup(this.layerGroup, m.getMarker());
                const { lat, lng } = m.getLatLng();
                bounds.push([lat, lng]);
            });

            this.polylines.forEach((pl) => {
                try {
                    const { lat: topLat, lng: topLng } = pl.getPolyline().getBounds().getSouthWest();
                    const { lat: bottomLat, lng: bottomLng } = pl.getPolyline().getBounds().getNorthEast();

                    bounds = [...bounds, [topLat, topLng], [bottomLat, bottomLng]];
                } catch (e) {
                    console.error('Incorrect polyline was provided');
                }
            });

            if (this.fit && bounds.length > 0) {
                this.mapApi.fitBounds(bounds);
            }
        });
    }

    private addMarkers() {
        const bounds = [];
        this.mapApi.clearLayerGroup(this.layerGroup);
        this.markers.forEach((m) => {
            this.mapApi.addToLayerGroup(this.layerGroup, m.getMarker());
            const { lat, lng } = m.getLatLng();
            bounds.push([lat, lng]);
        });
    }

    private addMarkerGroups() {
        this.markerGroups.forEach((markerGroup) => {
            this.mapApi.addToLayerGroup(this.layerGroup, markerGroup.getMarkerGroup());
        });
    }

    private addPolyline() {
        this.polylines.forEach((line) => this.mapApi.addToLayerGroup(this.layerGroup, line.getPolyline()));
    }
}
